/**
 * Name entry (SPEC §2.1): first name, 12 characters, profanity-filtered.
 *
 * The filter runs here for instant feedback and again on the server, which is
 * the one that matters. Twelve characters is a hard cap in the input as well as
 * the validator, because the name has to fit on a license card next to a face.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MAX_NAME_LENGTH } from '@idgame/protocol';
import type { PlayerId } from '@idgame/protocol';
import { clampName, validateName } from '../../lib/name.js';
import { useDraft, useDraftActions, useSubmitProfile } from '../../store/index.js';
import { LicenseCard } from '../../components/index.js';
import { Button, Notice, Screen } from '../../app/ui.js';

export function NameScreen() {
  const navigate = useNavigate();
  const { roomCode = '' } = useParams<{ roomCode: string }>();
  const draft = useDraft();
  const { setDraftName } = useDraftActions();
  const submitProfile = useSubmitProfile();

  const [touched, setTouched] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const validation = validateName(draft.name);
  const showProblem = touched && validation.problem !== null && validation.problem !== 'empty';

  const submit = () => {
    setTouched(true);
    if (!validation.ok) return;
    submitProfile({ name: validation.value });
    // SPEC §5.1 — the QR code is what the host's phone leads with, and the
    // lobby is where it lives. Host and guest land on the same screen; the
    // separate `/invite` screen that used to take the host's tap is gone.
    navigate(`/r/${roomCode}/lobby`, { replace: true });
  };

  return (
    <Screen
      eyebrow="Your card"
      title="Add your name."
      sub="First name only — it goes on the card."
      testId="name"
      footer={
        <Button form="name" type="submit" disabled={!validation.ok}>
          Into the lobby
        </Button>
      }
    >
      <form
        id="name"
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <input
          ref={inputRef}
          value={draft.name}
          onChange={(event) => setDraftName(clampName(event.target.value))}
          onBlur={() => setTouched(true)}
          maxLength={MAX_NAME_LENGTH}
          autoCapitalize="words"
          autoCorrect="off"
          autoComplete="given-name"
          spellCheck={false}
          enterKeyHint="done"
          aria-label="First name"
          aria-invalid={showProblem}
          placeholder="Sam"
          className="rounded-card w-full border border-line bg-cream px-4 py-4 text-2xl font-semibold tracking-tight text-ink outline-none placeholder:text-ink-soft/45 focus:border-2 focus:border-terracotta"
        />
        {showProblem ? <Notice tone="warn">{validation.message}</Notice> : null}
      </form>

      <div className="flex flex-1 flex-col items-center justify-center gap-3">
        {draft.photo ? (
          <>
            <LicenseCard
              variant="card"
              player={{
                id: 'self' as PlayerId,
                name: validation.ok ? validation.value : draft.name || 'You',
                photo: draft.photo,
              }}
              className="w-[13.5rem]"
            />
            <Button
              variant="quiet"
              full={false}
              onClick={() => navigate(`/r/${roomCode}/photo`, { replace: true })}
            >
              Change photo
            </Button>
          </>
        ) : null}
      </div>
    </Screen>
  );
}
